import { Button, HStack, useDisclosure } from '@chakra-ui/react';
import { ContentHeader, ContentLayout, Select } from 'Molecules';
import { useTranslation } from 'utils/translation';
import { InviteMembersModal } from './pieces';
import { useMemo, useState } from 'react';
import { useTeamMembers, TeamMembersFilterOption, useUserRole } from './TeamMembers.hooks';
import { TeamMembersTable } from './pieces/TeamMembersTable';
import { useCurrentCompany } from 'utils/hooks';
import { CompanyDetailsQuery_, UserRole_Enum_ } from 'models';
import { VisibleIf } from 'Atoms';

const TeamMembersHeader = ({
  company,
  filterBy,
  setFilterBy,
  openInviteMembersModal,
}: {
  filterBy: TeamMembersFilterOption;
  setFilterBy: (filterOption: TeamMembersFilterOption) => void;
  openInviteMembersModal: () => void;
  company: CompanyDetailsQuery_['company'][number] | undefined;
}) => {
  const { t } = useTranslation('teamMembers');
  const { role } = useUserRole();

  const filterOptions = useMemo(
    () =>
      Object.values(TeamMembersFilterOption).map((option) => ({
        value: option,
        label: t(`filterOptions.${option}`),
      })),
    []
  );

  return (
    <HStack width="100%" justifyContent="space-between" paddingY="16px">
      <HStack width="200px">
        <Select<{ value: TeamMembersFilterOption; label: string }>
          options={filterOptions}
          value={filterOptions.find((option) => option.value === filterBy)}
          onChange={(option) => setFilterBy(option?.value ?? TeamMembersFilterOption.All)}
        />
      </HStack>
      <VisibleIf condition={!company?.compassId}>
        <Button
          variant="primary"
          onClick={openInviteMembersModal}
          isDisabled={role !== UserRole_Enum_.Admin_}
        >
          {t('inviteMembersButton')}
        </Button>
      </VisibleIf>
    </HStack>
  );
};

export const TeamMembers = () => {
  const { t } = useTranslation('common');
  const { company, loading: companyLoading } = useCurrentCompany();

  const [filterBy, setFilterBy] = useState<TeamMembersFilterOption>(TeamMembersFilterOption.All);
  const { members, loading } = useTeamMembers(filterBy);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <ContentLayout
      isLoading={loading || companyLoading}
      header={
        <ContentHeader
          title={t('common:menu.teamMembers')}
          subtitle={`Manage users of ${company?.name ?? 'Group company'}`}
        />
      }
    >
      <TeamMembersHeader
        company={company}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        openInviteMembersModal={onOpen}
      />
      <TeamMembersTable members={members} setFilterBy={setFilterBy} company={company} />
      <InviteMembersModal isOpen={isOpen} onClose={onClose} />
    </ContentLayout>
  );
};
